import { useBasket } from '~/components/routes/campaign/useBasket';
import type { SelectableItem } from '~/models/CampaignModel';
import { OrderVariant } from '~/models/OrderVariant';
import { ProductType } from '~/models/product/ProductType';
import { BYOD_COMMERCIAL_PRODUCT_ID } from '~/store/campaign/byod.constants';
import { captureUXEvent, captureUXEventDebounced } from './productAnalytics';

const send = (event: Event) => {
  const { event: eventName, ...eventArgs } = event;
  console.debug('GA: before_send', eventName, eventArgs);
  window.dataLayer.push(event);
};

type OmitEvent<T> = Omit<T, 'event' | 'checkout_flow'>;
type BasketProducts = CampaignLoadedEvent['products'];

/**
 * Flat properties describing the current basket, added to every PostHog funnel event.
 * PostHog can only break down insights/funnels on a single scalar value, so the main
 * package is sent as its own property in addition to being part of the "products"
 * array. That is what makes conversion per main package possible without
 * array-contains filtering.
 *
 * NOTE: the fleksipakken choice is deliberately NOT here. A user can go back and
 * re-choose, which would leave several conflicting values across the funnel, so the
 * choice is only recorded on checkout submit (see trackPurchaseEvent).
 */
const getBasketTrackingProperties = () => {
  const { selectedProducts } = useBasket.getState();
  const subscription = selectedProducts.find((p) => p.productType === ProductType.Subscription);
  return {
    products: selectedProducts.map((p) => p.displayData.name),
    /** Main package display name, undefined until the user has picked one (step 1) */
    subscription: subscription?.displayData.name,
    /** Stable id for the main package. Survives marketing renames, unlike the name */
    subscriptionId: subscription?.offer.commercialProductId,
    premiumProducts: selectedProducts.filter((p) => p.productType === ProductType.Premium).map((p) => p.displayData.name),
  };
};

const trackStepChangeEvent = ({
  checkout_step,
  verification_method,
  funnelVersion,
}: OmitEvent<StepChangeEvent> & { funnelVersion?: number }) => {
  const basketState = useBasket.getState();
  const hasInternetProduct = basketState.hasInternetProduct();
  const v2Step = funnelVersion === 2 ? getFunnelV2Step(checkout_step) : undefined;
  const event: StepChangeEvent = {
    event: EventTypes.Checkout,
    checkout_step: v2Step ?? checkout_step,
    checkout_flow: hasInternetProduct ? 'internett+tv' : 'tv',
  };
  // add to params only if provided
  if (verification_method) {
    event.verification_method = verification_method;
  }
  send(event);

  // Posthog
  try {
    // add basket products to funnel step tracking
    const flow = hasInternetProduct ? 'internet' : 'regular';
    captureUXEventDebounced(
      getPostHogEventName(v2Step, checkout_step),
      Object.assign(
        getBasketTrackingProperties(),
        { flow },
        verification_method ? { verification_method } : undefined,
        funnelVersion ? { funnelVersion } : undefined,
      ),
    );
  } catch (e) {
    console.error('Error tracking step change event:', e);
  }
};
const getPostHogEventName = (v2Step: number | undefined, step: number) => {
  if (v2Step) return `bestill_funnel_step_${v2Step}_${TrackingPointsV2[v2Step]}`;
  return `bestill_funnel_step_${step}_${TrackingPoints[step]}`;
};
export const getFunnelV2Step = (step: number) => {
  const num = TrackingPoints[step];
  if (step === TrackingPoints.CustomerFilter) {
    return TrackingPointsV2.ProductComparison;
  }
  return TrackingPointsV2[num as keyof typeof TrackingPointsV2];
};

type StepChangeEventArgs =
  | {
      trackingPoint: TrackingPoints.ContactInformation;
      verificationProvider: 'bankid' | 'vipps';
      products: BasketProducts;
      funnelVersion?: number;
    }
  | {
      trackingPoint: TrackingPoints.Equipment;
      products: BasketProducts;
      funnelVersion?: number;
    }
  | {
      trackingPoint: Exclude<TrackingPoints, TrackingPoints.ContactInformation | TrackingPoints.Equipment>;
      funnelVersion?: number;
    };
export const trackChangeTrackingPointEvent = (params: StepChangeEventArgs) => {
  if (params.trackingPoint === TrackingPoints.ContactInformation) {
    // Special case: track verification provider after callback
    trackStepChangeEvent({
      checkout_step: params.trackingPoint,
      verification_method: params.verificationProvider,
      funnelVersion: params.funnelVersion,
    });
  } else {
    trackStepChangeEvent({ checkout_step: params.trackingPoint, funnelVersion: params.funnelVersion });
  }
  // On navigation to 2nd and 3rd step, track products
  if (params.trackingPoint === TrackingPoints.Equipment || params.trackingPoint === TrackingPoints.ContactInformation) {
    send({
      event: EventTypes.AddToCart,
      ecommerce: {
        value: params.products.reduce((acc, product) => acc + product.price * product.quantity, 0),
        items: params.products.map(({ id, name, category, variant, price, quantity }) => ({
          item_id: id.toString(),
          item_name: name,
          item_category: category,
          item_variant: variant,
          price,
          quantity,
        })),
      },
    });
  }
};

export const trackToggleOpenBasketEvent = (isOpen: boolean) => {
  send({
    event: isOpen ? EventTypes.ExpandableBasketOpen : EventTypes.ExpandableBasketClose,
    ecommerce: undefined,
  } satisfies ExpandableBasketEvent);
};

export const trackPurchaseEvent = ({
  products,
  partnerName,
  verification_method,
  delayedActivation,
  ...rest
}: Omit<PurchaseEvent['ecommerce'], 'items'> & {
  products: BasketProducts;
  partnerName: string | undefined;
  verification_method: string;
  funnelVersion?: number;
  delayedActivation: boolean;
}) => {
  try {
    send({
      event: EventTypes.Purchase,
      checkout_flow: products.some((p) => p.category === 'internet') ? 'internett+tv' : 'tv',
      ecommerce: {
        ...rest,
        items: products.map((product) => ({
          item_id: product.id.toString(),
          item_name: product.name,
          price: product.price,
          affiliation: partnerName,
        })),
      },
    } satisfies PurchaseEvent);
    // Posthog
    const allProducts = products.map((p) => p.name);
    const installation = products.filter((p) => p.category === 'installation').map((p) => p.name);
    const premium = products.filter((p) => p.category === 'premium').map((p) => p.name);
    const receivers = products.filter((p) => p.category === 'receiver').map((p) => p.name);
    const equipment = products.filter((p) => p.category === 'equipment').map((p) => p.name);
    const selectedByod = products.some((p) => p.id === BYOD_COMMERCIAL_PRODUCT_ID);
    const subscription = products.find((p) => p.category === ProductType.Subscription);
    const { flexipakkenChoice } = useBasket.getState();

    captureUXEvent(
      `bestill_funnel_step_checkout_posted`,
      {
        subscription: subscription?.name,
        subscriptionId: subscription?.id,
        // The fleksipakken alternative the user actually ordered. Only recorded here,
        // so going back and re-choosing during the flow cannot skew it.
        flexipakkenChoice: flexipakkenChoice?.name,
        flexipakkenChoiceKind: flexipakkenChoice?.kind,
        flexipakkenChannelId: flexipakkenChoice?.channelId,
        allProducts,
        installation,
        premium,
        receivers,
        equipment,
        selectedByod,
        verification_method,
        costFirstYear: rest.value,
        funnelVersion: rest.funnelVersion,
        delayedActivation,
        ispPartner: partnerName,
        flow: products.some((p) => p.category === 'internet') ? 'internet' : 'regular',
      },
      { send_instantly: true },
    );
  } catch (e) {
    console.error('Error tracking purchase event:', e);
  }
};

export enum TrackingPoints {
  CustomerFilter = 1,
  ProductComparison = 2,
  Content = 3,
  Equipment = 4,
  // only used in new flow
  Internet = 4.5,
  Verification = 5,
  ContactInformation = 6,
  Summary = 7,
}
export enum TrackingPointsV2 {
  ProductComparison = 1,
  Content = 2,
  Equipment = 3,
  // only used in new flow
  Internet = 4,
  Verification = 5,
  ContactInformation = 6,
  Summary = 7,
}

enum EventTypes {
  Checkout = 'checkout',
  CampaignLoaded = 'campaignLoaded',
  AddToCart = 'add_to_cart',
  ExpandableBasketOpen = 'expandable-basket-open',
  ExpandableBasketClose = 'expandable-basket-close',
  Purchase = 'purchase',
  CustomerFilter = 'customer_filter',
}
type VerificationMethod = 'bankid' | 'vipps';
interface StepChangeEvent {
  event: EventTypes.Checkout;
  checkout_step: number;
  checkout_flow: 'internett+tv' | 'tv';
  verification_method?: VerificationMethod;
}

interface CampaignLoadedEvent {
  event: EventTypes.CampaignLoaded;
  campaignCode: string;
  campaignVariant: string;
  products: Array<{ id: number; name: string; price: number; category: ProductType; quantity: number; variant?: OrderVariant }>;
  partner: string;
}

interface AddProductEvent {
  event: EventTypes.AddToCart;
  ecommerce: {
    value: number;
    add: {
      products: Array<{
        item_id: string;
        item_name: string;
        item_category: string;
        item_variant?: string;
        price: number;
        quantity: number;
      }>;
    };
  };
}

interface CustomerFilterEvent {
  event: EventTypes.CustomerFilter;
  step: number;
  distribution_model?: 'DTT' | 'IP';
  distribution_partner?: string;
  dwelling_unit?: 'SDU' | 'MDU';
}

interface ExpandableBasketEvent {
  event: EventTypes.ExpandableBasketOpen | EventTypes.ExpandableBasketClose;
  ecommerce: undefined;
}

/**
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#make_a_purchase_or_issue_a_refund
 */
interface PurchaseEvent {
  event: EventTypes.Purchase;
  checkout_flow: 'internett+tv' | 'tv';
  ecommerce: {
    currency: 'NOK';
    /** The unique identifier of a transaction. Avoids duplicate events */
    transaction_id: string;
    /** Monetary value of event (cost first year) */
    value: number;
    /** The coupon name/code associated with the event. */
    coupon?: string;
    /** Shipping cost associated with a transaction. */
    shipping?: number;
    /** Tax cost associated with a transaction. */
    tax?: number;
    /** Purchased items */
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      /** A product affiliation to designate a supplying company or brick and mortar store location. */
      affiliation?: string;
    }>;
  };
}
type Event =
  | StepChangeEvent
  | CampaignLoadedEvent
  | AddProductEvent
  | ExpandableBasketEvent
  | PurchaseEvent
  | CustomerFilterEvent
  | { event: string; [key: string]: any }; // TODO: type up isp events

/** Main package (Fleksipakken, Rikspakken, ...) selected on the comparison page */
export const trackSelectSubscriptionEvent = (props: { name: string; campaignUrl: string; price: number }) => {
  captureUXEventDebounced('rtv_select_subscription', {
    subscription: props.name,
    campaignUrl: props.campaignUrl,
    price: props.price,
  });
};

/**
 * Products were added to or removed from the basket. Gives us per-addon uptake
 * ("Legg til ekstra innhold") instead of only seeing the end result on checkout.
 *
 * Takes the selection from *before* the mutation and diffs it against the store, rather
 * than trusting the click: selectProduct applies bundle rules on top of what was asked
 * for. It refuses to add when maxItems is reached, replaces the other product in a
 * single-selection bundle, drops conflicting products, and may swap offers in a
 * best-discount bundle. Tracking the requested toggle would report additions that never
 * happened and miss every rule-driven removal.
 *
 * Call it after the mutation; selectProduct updates the store synchronously.
 */
export const trackProductSelectionChange = (selectedBefore: SelectableItem[]) => {
  const selectedAfter = useBasket.getState().selectedProducts;
  const idsBefore = new Set(selectedBefore.map((p) => p.id));
  const idsAfter = new Set(selectedAfter.map((p) => p.id));
  // so addon uptake can be sliced per main package
  const subscription = selectedAfter.find((p) => p.productType === ProductType.Subscription)?.displayData.name;

  const changes = [
    ...selectedAfter.filter((p) => !idsBefore.has(p.id)).map((product) => ({ product, action: 'add' as const })),
    ...selectedBefore.filter((p) => !idsAfter.has(p.id)).map((product) => ({ product, action: 'remove' as const })),
  ];

  for (const { product, action } of changes) {
    captureUXEventDebounced(
      'rtv_toggle_product_selection',
      {
        action,
        productName: product.displayData.name,
        productId: product.offer.commercialProductId,
        productType: product.productType,
        price: product.pricing.price,
        originalPrice: product.pricing.originalPrice,
        subscription,
      },
      // per product: one mutation can change several products in the same tick
      { key: `rtv_toggle_product_selection:${product.id}` },
    );
  }
};

/**
 * The user is being handed off to BankID/Vipps. This is a full page navigation away, so
 * it is the last event of this PostHog session - identity does not survive the round trip
 * while persistence is "memory".
 *
 * Four things keep the event from being lost to the navigation: the posthog client is
 * already loading, because the funnel step event on mount of this same step starts it
 * before the user can pick a provider (a beacon can only be sent once the client is
 * there); it is sent before the init request, so that network round trip buys more time;
 * it skips the batch queue; and it goes out over sendBeacon, which the browser delivers
 * even once the document is being torn down. Deliberately not awaited - the verification
 * handoff must not wait on analytics loading, let alone fail with it.
 *
 * Pair it with bestill_funnel_step_6_ContactInformation (the first event after returning)
 * to measure drop-off across the verification jump at cohort level.
 */
export const trackStartVerificationEvent = (provider: string) => {
  captureUXEvent(
    'rtv_start_verification',
    Object.assign(getBasketTrackingProperties(), { verification_method: provider.toLowerCase() }),
    { send_instantly: true, transport: 'sendBeacon' },
  );
};

/** Where the user asked for more information about the Fleksipakken choice */
export type FlexipakkenInfoSource = 'how-it-works' | 'all-tv-channels';
const flexipakkenInfoLinkText: Record<FlexipakkenInfoSource, string> = {
  'how-it-works': 'Hvordan fungerer dette?',
  'all-tv-channels': 'Se alle TV-kanaler',
};

/** The user wants more information before making the Fleksipakken choice */
export const trackFlexipakkenMoreInformationEvent = (source: FlexipakkenInfoSource) => {
  captureUXEvent('rtv_flexipakken_more_information', {
    source,
    linkText: flexipakkenInfoLinkText[source],
    // the choice the user had made when asking for more information, if any
    currentChoice: useBasket.getState().flexipakkenChoice?.name,
  });
};

/** Internet sales specific tracking */
export const trackInternetSalesView = () => captureUXEvent('rtv_view_internet_sales');

export const trackClickCheckAddress = (props: { city: string; postal_code: string }) => {
  // Bruker velger "Sjekk tilgjenglighet" knappen på hovesiden (/internett)
  send({
    event: 'verify_ngt_availability',
    city: props.city, //by
    postal_code: props.postal_code, //postnummer
  });
  captureUXEvent('rtv_click_check_address', {
    city: props.city,
    postal_code: props.postal_code,
  });
};

export const trackAddressAvailabilityResult = (props: { city: string; postal_code: string; ngt_available: boolean }) => {
  // Svar på tilgjengelighet
  send({
    event: 'ngt_availability',
    city: props.city, //by
    postal_code: props.postal_code, //postnummer
    ngt_available: props.ngt_available, //true or false
  });
  captureUXEvent('rtv_address_availability_result', {
    city: props.city,
    postal_code: props.postal_code,
    ngt_available: props.ngt_available,
  });
};

export const trackClickCheckOtherAddress = () => {
  // Bruker velger å gjøre et nytt oppslag ("Søk på en annen adresse" knappen)
  send({
    event: 'new_availability_check',
  });
  captureUXEvent('rtv_click_check_other_address');
};

export const trackSelectInternetBundle = (props: {
  city: string;
  postal_code: string;
  bundle: { name: string; price: number; id?: string };
}) => {
  // Bruker velger en av CTA'ene (Ngt tilgjengelig)
  send({
    event: 'select_item',
    city: props.city, //by
    postal_code: props.postal_code, //postnummer
    ecommerce: {
      items: [
        {
          item_name: props.bundle.name,
          item_id: props.bundle.id, //hvis tilgjengelig
          price: props.bundle.price * 12, // pris * 12
        },
      ],
    },
  });
  captureUXEvent('rtv_select_internet_bundle', {
    city: props.city,
    postal_code: props.postal_code,
    bundle: props.bundle.name,
  });
};

export const trackClickInternetCTA = (props: { ngt_available: boolean; link_text: string; link_url?: string }) => {
  // Bruker velger generisk CTA ("Se TV-pakker ->", "Les mer")
  send({
    event: 'cta_button_click',
    ngt_available: props.ngt_available,
    link_text: props.link_text,
    link_url: props.link_url,
  });
  captureUXEvent('rtv_click_internet_cta', {
    ngt_available: props.ngt_available,
    link_text: props.link_text,
    link_url: props.link_url,
  });
};
